import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, ZodiacSign, PlanetPosition, PlanetName, ChartStyle } from '../types/kundali';
import { ZODIAC_HI, getPlanetShortCode } from '../utils/language';
import { 
  Activity, 
  Sparkles, 
  Compass, 
  CheckCircle, 
  Info, 
  ArrowUpRight, 
  Calendar, 
  Clock, 
  RotateCcw, 
  SlidersHorizontal,
  LayoutGrid,
  ZoomIn,
  ZoomOut,
  Zap,
  Target,
  Layers,
  ArrowRight,
  Sun,
  Moon,
  ShieldCheck,
  ShieldAlert,
  FileCheck2,
  ChevronDown,
  ChevronUp,
  Eye,
  AlertTriangle
} from 'lucide-react';
import { AlertBadge } from './AlertBadge';
import { calculateLivePlanetaryEphemeris, ZODIAC_LORDS, NAKSHATRAS } from '../utils/vedicEngine';
import { formatOrdinal, shiftMonthsImmutable, shiftYearsImmutable } from '../utils/dateUtils';
import { GocharDualChart, GocharChartLayout } from './GocharDualChart';
import { calculateAllPlanetaryTransitPeriods, PlanetaryTransitPeriod } from '../utils/gocharPeriodEngine';
import { evaluateAllGocharVedha, evaluateGocharVedha, VedhaStatusResult } from '../utils/gocharVedhaEngine';
import { EvidenceProvenanceDrawer } from './v600/EvidenceProvenanceDrawer';
import { GocharDrishtiMatrix } from './v600/GocharDrishtiMatrix';
import { UnifiedGocharTimeline } from './v600/UnifiedGocharTimeline';

export type GocharPerspective = 'lagna' | 'moon' | 'sun' | 'tri_lagna';

interface GocharViewProps {
  kundali: CompleteKundali;
  lang?: Language;
  onNavigateToTab?: (tabId: string) => void;
  targetAnalysisDateTime?: Date;
  onTargetAnalysisDateTimeChange?: (date: Date) => void;
}

export const GocharView: React.FC<GocharViewProps> = ({ 
  kundali, 
  lang = 'en', 
  onNavigateToTab,
  targetAnalysisDateTime: externalMoment,
  onTargetAnalysisDateTimeChange
}) => {
  const isHi = lang === 'hi';

  // Helper to format Date into "YYYY-MM-DD"
  const formatDateForInput = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper to format Date into "HH:mm"
  const formatTimeForInput = (d: Date): string => {
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${mins}`;
  };

  const initialDate = externalMoment || new Date();

  // State for dynamic date & time selection
  const [selectedDate, setSelectedDate] = useState<string>(() => formatDateForInput(initialDate));
  const [selectedTime, setSelectedTime] = useState<string>(() => formatTimeForInput(initialDate));
  const [chartStyle, setChartStyle] = useState<ChartStyle>('north');
  const [layoutMode, setLayoutMode] = useState<GocharChartLayout>('grid');
  const [chartScale, setChartScale] = useState<number>(100);
  const [showDegrees, setShowDegrees] = useState<boolean>(true);

  // Phase 1 P1 States: Perspective, Vedha, Cross-Highlight, and Evidence Drawer
  const [selectedPerspective, setSelectedPerspective] = useState<GocharPerspective>('lagna');
  const [selectedTransitPlanet, setSelectedTransitPlanet] = useState<PlanetName | null>('Jupiter');
  const [showEvidenceDrawer, setShowEvidenceDrawer] = useState<boolean>(false);
  const [evidenceFocusPlanet, setEvidenceFocusPlanet] = useState<PlanetName | null>(null);

  // Sync with external moment if changed
  React.useEffect(() => {
    if (externalMoment) {
      setSelectedDate(formatDateForInput(externalMoment));
      setSelectedTime(formatTimeForInput(externalMoment));
    }
  }, [externalMoment]);

  // Construct effective transit Date object
  const effectiveTransitDate = useMemo(() => {
    try {
      const [year, month, day] = selectedDate.split('-').map(Number);
      const [hour, minute] = selectedTime.split(':').map(Number);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        return new Date(year, month - 1, day, isNaN(hour) ? 12 : hour, isNaN(minute) ? 0 : minute, 0);
      }
    } catch {
      // fallback
    }
    return externalMoment || new Date();
  }, [selectedDate, selectedTime, externalMoment]);

  const updateDateTime = (newDateStr: string, newTimeStr: string) => {
    setSelectedDate(newDateStr);
    setSelectedTime(newTimeStr);
    if (onTargetAnalysisDateTimeChange) {
      try {
        const [year, month, day] = newDateStr.split('-').map(Number);
        const [hour, minute] = newTimeStr.split(':').map(Number);
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          const d = new Date(year, month - 1, day, isNaN(hour) ? 12 : hour, isNaN(minute) ? 0 : minute, 0);
          onTargetAnalysisDateTimeChange(d);
        }
      } catch {}
    }
  };

  // Format display string for current selected transit time
  const transitDateFormatted = useMemo(() => {
    const d = effectiveTransitDate;
    const day = d.getDate().toString().padStart(2, '0');
    const monthNamesEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthNamesHi = ['जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर'];
    const month = isHi ? monthNamesHi[d.getMonth()] : monthNamesEn[d.getMonth()];
    const year = d.getFullYear();
    const hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    const timeStr = `${hours12.toString().padStart(2, '0')}:${minutes} ${ampm}`;
    return `${day} ${month} ${year}, ${timeStr}`;
  }, [effectiveTransitDate, isHi]);

  // Native's Moon Sign, Lagna Sign, and Sun Sign (Tri-Lagna Foundation)
  const moonPlanet = kundali.planets.find((p) => p.name === 'Moon');
  const moonSignIndex = moonPlanet ? moonPlanet.signIndex : 0;
  const moonSignName = moonPlanet ? moonPlanet.sign : 'Aries';

  const lagnaHouse = kundali.houses[0];
  const lagnaSignIndex = lagnaHouse ? lagnaHouse.signIndex : 0;
  const lagnaSignName = lagnaHouse ? lagnaHouse.sign : 'Aries';

  const sunPlanet = kundali.planets.find((p) => p.name === 'Sun');
  const sunSignIndex = sunPlanet ? sunPlanet.signIndex : 0;
  const sunSignName = sunPlanet ? sunPlanet.sign : 'Aries';

  const nodeConvention = kundali.nodeConvention || kundali.birthDetails?.nodeConvention || 'mean';

  // Calculate live dynamic planetary ephemeris for the selected time
  const dynamicEphemeris = useMemo(() => {
    return calculateLivePlanetaryEphemeris(effectiveTransitDate, nodeConvention);
  }, [effectiveTransitDate, nodeConvention]);

  // Helper to calculate house distance (1-indexed)
  const calculateHouseDistance = (natalSignIdx: number, transitSignIdx: number) => {
    let house = transitSignIdx - natalSignIdx + 1;
    if (house <= 0) house += 12;
    return house;
  };

  // Classical Gochar Vedha (Obstruction) Evaluation for all planets from Moon
  const allVedhaStatus: Record<PlanetName, VedhaStatusResult> = useMemo(() => {
    return evaluateAllGocharVedha(moonSignIndex, dynamicEphemeris);
  }, [moonSignIndex, dynamicEphemeris]);

  // Active Vimshottari Dasha Context for Evidence Drawer
  const activeMD = kundali.dashas?.find(d => d.isCurrent) || kundali.dashas?.[0] || null;
  const activeAD = activeMD?.antardashas?.find(ad => ad.isCurrent) || activeMD?.antardashas?.[0] || null;

  // Compute Cross-Highlight Context for Selected Transit Planet
  const selectedTransitCrossHighlight = useMemo(() => {
    if (!selectedTransitPlanet) return null;
    const tp = dynamicEphemeris.find(p => p.planet === selectedTransitPlanet);
    if (!tp) return null;

    const houseFromLagna = calculateHouseDistance(lagnaSignIndex, tp.signIndex);
    const houseFromMoon = calculateHouseDistance(moonSignIndex, tp.signIndex);
    const houseFromSun = calculateHouseDistance(sunSignIndex, tp.signIndex);

    // Parashari aspected houses from Lagna
    const aspects: number[] = [((houseFromLagna - 1 + 6) % 12) + 1]; // 7th aspect
    if (selectedTransitPlanet === 'Jupiter' || selectedTransitPlanet === 'Rahu' || selectedTransitPlanet === 'Ketu') {
      aspects.push(((houseFromLagna - 1 + 4) % 12) + 1); // 5th
      aspects.push(((houseFromLagna - 1 + 8) % 12) + 1); // 9th
    } else if (selectedTransitPlanet === 'Saturn') {
      aspects.push(((houseFromLagna - 1 + 2) % 12) + 1); // 3rd
      aspects.push(((houseFromLagna - 1 + 9) % 12) + 1); // 10th
    } else if (selectedTransitPlanet === 'Mars') {
      aspects.push(((houseFromLagna - 1 + 3) % 12) + 1); // 4th
      aspects.push(((houseFromLagna - 1 + 7) % 12) + 1); // 8th
    }
    const uniqueAspects = Array.from(new Set(aspects)).sort((a, b) => a - b);

    // Natal planets in occupied house or aspected houses
    const contactedNatalPlanets: string[] = [];
    (kundali.planets || []).forEach(np => {
      if (np.name !== 'Ascendant') {
        if (np.house === houseFromLagna) {
          contactedNatalPlanets.push(`${np.name} (Yuti in H${np.house})`);
        } else if (uniqueAspects.includes(np.house)) {
          contactedNatalPlanets.push(`${np.name} (Drishti on H${np.house})`);
        }
      }
    });

    const vedhaInfo = allVedhaStatus[selectedTransitPlanet];

    return {
      planet: selectedTransitPlanet,
      sign: tp.sign,
      degreeFormatted: tp.formattedDegree,
      isRetrograde: tp.isRetrograde,
      houseFromLagna,
      houseFromMoon,
      houseFromSun,
      aspectHouses: uniqueAspects,
      contactedNatalPlanets,
      vedhaInfo
    };
  }, [selectedTransitPlanet, dynamicEphemeris, lagnaSignIndex, moonSignIndex, sunSignIndex, kundali.planets, allVedhaStatus]);

  // Build full transit PlanetPosition list to feed into charts & comparative inspection
  const transitPlanetsForChart: PlanetPosition[] = useMemo(() => {
    return dynamicEphemeris.map((tp) => {
      const houseFromLagna = calculateHouseDistance(lagnaSignIndex, tp.signIndex);
      const nakIdx = Math.floor(tp.longitude / (360 / 27));
      const nakObj = NAKSHATRAS[nakIdx] || { name: 'Ashwini', lord: 'Ketu' };
      const degInNak = tp.longitude % (360 / 27);
      const pada = Math.floor(degInNak / (360 / 108)) + 1;

      return {
        name: tp.planet,
        symbol: getPlanetShortCode(tp.planet, lang),
        longitude: tp.longitude,
        sign: tp.sign,
        signIndex: tp.signIndex,
        degreeInSign: tp.degreeInSign,
        degreeFormatted: tp.formattedDegree,
        house: houseFromLagna,
        signLord: ZODIAC_LORDS[tp.sign] || 'Sun',
        nakshatra: nakObj.name,
        nakshatraIndex: nakIdx,
        nakshatraLord: nakObj.lord,
        pada,
        isRetrograde: tp.isRetrograde,
        isCombust: false,
        dignity: 'Neutral'
      };
    });
  }, [dynamicEphemeris, lagnaSignIndex, lang]);

  // Jupiter Transit Analysis at Selected Time
  const jupiterTransit = dynamicEphemeris.find((p) => p.planet === 'Jupiter') || {
    planet: 'Jupiter' as PlanetName,
    sign: 'Gemini' as ZodiacSign,
    signIndex: 2,
    degreeInSign: 12.5,
    formattedDegree: '12° 30\'',
    longitude: 72.5,
    isRetrograde: false
  };
  const jupiterHouseFromMoon = calculateHouseDistance(moonSignIndex, jupiterTransit.signIndex);
  const jupiterHouseFromLagna = calculateHouseDistance(lagnaSignIndex, jupiterTransit.signIndex);

  // Favorable houses for Jupiter transit from Moon: 2, 5, 7, 9, 11
  const isJupiterAuspicious = [2, 5, 7, 9, 11].includes(jupiterHouseFromMoon);

  // Saturn Transit Analysis at Selected Time
  const saturnTransit = dynamicEphemeris.find((p) => p.planet === 'Saturn') || {
    planet: 'Saturn' as PlanetName,
    sign: 'Pisces' as ZodiacSign,
    signIndex: 11,
    degreeInSign: 4.3,
    formattedDegree: '04° 18\'',
    longitude: 334.3,
    isRetrograde: false
  };
  const saturnHouseFromMoon = calculateHouseDistance(moonSignIndex, saturnTransit.signIndex);
  const saturnHouseFromLagna = calculateHouseDistance(lagnaSignIndex, saturnTransit.signIndex);

  // Favorable houses for Saturn transit from Moon: 3, 6, 11
  const isSaturnAuspicious = [3, 6, 11].includes(saturnHouseFromMoon);

  // Sade Sati Status Calculation for selected time
  // Sade Sati occurs when Saturn transits 12th, 1st, or 2nd house from Moon.
  let sadeSatiPhase: 'none' | 'rising' | 'peak' | 'setting' = 'none';
  if (saturnHouseFromMoon === 12) {
    sadeSatiPhase = 'rising'; // 1st Phase (Saturn in 12th from Moon)
  } else if (saturnHouseFromMoon === 1) {
    sadeSatiPhase = 'peak'; // 2nd Phase (Saturn over Moon)
  } else if (saturnHouseFromMoon === 2) {
    sadeSatiPhase = 'setting'; // 3rd Phase (Saturn in 2nd from Moon)
  }

  // Dhaiya (Small Panoti): Saturn in 4th or 8th house from Moon
  const isDhaiya = saturnHouseFromMoon === 4 || saturnHouseFromMoon === 8;

  // Quick preset offsets (Now, +1 Month, +6 Months, +1 Year, Birth Time)
  const setPreset = (type: 'now' | 'birth' | 'plus1m' | 'plus6m' | 'plus1y' | 'minus1m' | 'minus1y') => {
    if (type === 'now') {
      const now = new Date();
      updateDateTime(formatDateForInput(now), formatTimeForInput(now));
      return;
    }
    if (type === 'birth') {
      updateDateTime(kundali.birthDetails.date, kundali.birthDetails.time);
      return;
    }
    let target = effectiveTransitDate;
    if (type === 'plus1m') {
      target = shiftMonthsImmutable(effectiveTransitDate, 1);
    } else if (type === 'minus1m') {
      target = shiftMonthsImmutable(effectiveTransitDate, -1);
    } else if (type === 'plus6m') {
      target = shiftMonthsImmutable(effectiveTransitDate, 6);
    } else if (type === 'plus1y') {
      target = shiftYearsImmutable(effectiveTransitDate, 1);
    } else if (type === 'minus1y') {
      target = shiftYearsImmutable(effectiveTransitDate, -1);
    }
    updateDateTime(formatDateForInput(target), formatTimeForInput(target));
  };

  // Jupiter Detailed Interpretation per House
  const getJupiterHouseText = (houseNum: number) => {
    switch (houseNum) {
      case 1:
        return isHi
          ? 'प्रथम भाव गोचर: स्वास्थ्य वृद्धि, सात्विक विचार, धार्मिकता एवं आत्मसम्मान में वृद्धि। अतिशय आत्मविश्वास से बचें।'
          : '1st House Transit: Self-realization, spiritual inclination, and newfound wisdom. Maintain modesty to avoid over-confidence.';
      case 2:
        return isHi
          ? 'द्वितीय भाव गोचर (अत्यंत शुभ): धन-धान्य वृद्धि, परिवार में मांगलिक कार्य, वाणी में माधुर्य तथा संचित निधि में वृद्धि।'
          : '2nd House Transit (Highly Auspicious): Financial prosperity, family celebrations, sweet speech, and growth in savings.';
      case 3:
        return isHi
          ? 'तृतीय भाव गोचर: पराक्रम में वृद्धि, छोटी यात्राएं, भाइयों का सहयोग। व्यर्थ के विवादों से बचें।'
          : '3rd House Transit: Increased courage, short journeys, and support from siblings. Avoid unnecessary arguments.';
      case 4:
        return isHi
          ? 'चतुर्थ भाव गोचर: गृह-सुख, वाहन एवं भूमि लाभ। माता के स्वास्थ्य का ध्यान रखें एवं गृह शांति बनाए रखें।'
          : '4th House Transit: Domestic happiness, property/vehicle gains, and peace at home. Mind mother\'s health.';
      case 5:
        return isHi
          ? 'पंचम भाव गोचर (अत्यंत शुभ): संतान सुख, विद्या-बुद्धि में असाधारण प्रगति, शेयर/निवेश में लाभ एवं शुभ योग।'
          : '5th House Transit (Highly Auspicious): Joy from children, academic excellence, sharp intellect, and favorable investments.';
      case 6:
        return isHi
          ? 'षष्ठ भाव गोचर: शत्रुओं पर विजय, ऋणमुक्ति एवं प्रतियोगी परीक्षाओं में सफलता। स्वास्थ्य के प्रति सतर्क रहें।'
          : '6th House Transit: Victory over adversaries, debt reduction, and competitive success. Maintain healthy habits.';
      case 7:
        return isHi
          ? 'सप्तम भाव गोचर (अत्यंत शुभ): विवाह योग, व्यापारिक साझेदारी में भारी सफलता एवं दांपत्य जीवन में मधुरता।'
          : '7th House Transit (Highly Auspicious): Marriage prospects, booming business partnerships, and harmony in relationships.';
      case 8:
        return isHi
          ? 'अष्टम भाव गोचर: गूढ़ विद्या, शोध एवं अचानक धन लाभ। स्वास्थ्य एवं यात्राओं में सावधानी रखें।'
          : '8th House Transit: Interest in occult sciences, research gains, and sudden inherits. Drive safely and care for health.';
      case 9:
        return isHi
          ? 'नवम भाव गोचर (अत्यंत शुभ): भाग्योदय, तीर्थ यात्रा, गुरुजनों का आशीर्वाद एवं कार्यक्षेत्र में पदोन्नति।'
          : '9th House Transit (Highly Auspicious): Great fortune, long spiritual pilgrimages, mentorship, and career ascension.';
      case 10:
        return isHi
          ? 'दशम भाव गोचर: कार्यक्षेत्र में बड़ा बदलाव, नई जिम्मेदारियां एवं प्रतिष्ठा। अत्यधिक कार्यभार रह सकता है।'
          : '10th House Transit: Major professional developments, public recognition, and status upgrades. Manage workload well.';
      case 11:
        return isHi
          ? 'एकादश भाव गोचर (अत्यंत शुभ): सर्वमनोकामना पूर्ति, प्रचुर धन लाभ, मित्रों एवं वरिष्ठों से भारी लाभ।'
          : '11th House Transit (Highly Auspicious): Fulfillment of desires, abundant wealth inflow, and powerful influential networks.';
      case 12:
        return isHi
          ? 'द्वादश भाव गोचर: धार्मिक कार्यों पर व्यय, विदेश यात्रा, योग-ध्यान में रुचि। अनियंत्रित खर्चों पर नियंत्रण रखें।'
          : '12th House Transit: Spiritual expenditures, overseas travel, meditation, and detachment. Control impulsive spending.';
      default:
        return '';
    }
  };

  // Saturn Detailed Interpretation per House
  const getSaturnHouseText = (houseNum: number) => {
    switch (houseNum) {
      case 1:
        return isHi
          ? 'चंद्र पर शनि (साढ़ेसाती शिखर चरण): मानसिक दबाव, अत्यधिक परिश्रम तथा स्वास्थ्य में भारी अनुशासन की आवश्यकता।'
          : 'Saturn over Moon (Sade Sati Peak): Heavy mental responsibility, need for strict discipline, patience, and perseverance.';
      case 2:
        return isHi
          ? 'द्वितीय भाव शनि (साढ़ेसाती अस्त चरण): पारिवारिक जिम्मेदारियां, संचित धन प्रबंधन एवं वाणी पर नियंत्रण।'
          : '2nd House Saturn (Sade Sati Setting): Family duties, disciplined financial budgeting, and thoughtful communication.';
      case 3:
        return isHi
          ? 'तृतीय भाव शनि (शुभ गोचर): असाधारण पराक्रम, शत्रुओं का दमन, पेशेवर मजबूती तथा कार्यक्षेत्र में प्रभुत्व।'
          : '3rd House Saturn (Auspicious): Formidable courage, competitive dominance, career stabilization, and grit.';
      case 4:
        return isHi
          ? 'चतुर्थ भाव शनि (शनि की ढैय्या): मानसिक अशान्ति, स्थान परिवर्तन, संपत्ति/वाहन संबंधी कार्यों में विलंब।'
          : '4th House Saturn (Dhaiya - Panoti): Domestic shifts, delay in real estate decisions, need for emotional resilience.';
      case 5:
        return isHi
          ? 'पंचम भाव शनि: अध्ययन में कड़ी मेहनत, संतान संबंधी जिम्मेदारियां एवं सट्टा/निवेश से दूरी।'
          : '5th House Saturn: Disciplined learning, elevated parenting duties, and caution in speculative markets.';
      case 6:
        return isHi
          ? 'षष्ठ भाव शनि (शुभ गोचर): रोगों एवं मुकदमों पर पूर्ण विजय, अनुशासन से सफलता एवं ऋणमुक्ति।'
          : '6th House Saturn (Auspicious): Total victory over challenges, athletic stamina, debt relief, and strong immunity.';
      case 7:
        return isHi
          ? 'सप्तम भाव शनि: साझीदारियों में गंभीरता, दांपत्य में परिपक्वता तथा व्यापारिक समझौतों की समीक्षा।'
          : '7th House Saturn: Maturity in marriage, realistic business commitments, and long-term legal foundations.';
      case 8:
        return isHi
          ? 'अष्टम भाव शनि (शनि की ढैय्या): गूढ़ परिवर्तन, स्वास्थ्य देखभाल, खान-पान एवं वाहन चलाने में विशेष सावधानी।'
          : '8th House Saturn (Dhaiya - Panoti): Deep transformational period, focus on longevity, health, and slow driving.';
      case 9:
        return isHi
          ? 'नवम भाव शनि: धार्मिक अनुशासन, उच्च शिक्षा में कड़ा परिश्रम तथा पिता/गुरुजनों का सम्मान।'
          : '9th House Saturn: Structured higher studies, spiritual discipline, and duties toward elders.';
      case 10:
        return isHi
          ? 'दशम भाव शनि: करियर में भारी कर्म-संस्कार, पद-प्रतिष्ठा में वृद्धि परंतु अत्यधिक उत्तरदायित्व।'
          : '10th House Saturn: Professional milestones earned through hard work, high authority, and heavy duties.';
      case 11:
        return isHi
          ? 'एकादश भाव शनि (शुभ गोचर): स्थाई संपत्ति लाभ, दीर्घकालिक स्रोतों से निरंतर आय एवं महत्वाकांक्षा पूर्ति।'
          : '11th House Saturn (Auspicious): Steady long-term financial growth, realization of big goals, and stable profits.';
      case 12:
        return isHi
          ? 'द्वादश भाव शनि (साढ़ेसाती उदय चरण): विदेश गमन, ध्यान-एकांत, अनिद्रा से बचाव तथा बजट अनुशासन।'
          : '12th House Saturn (Sade Sati Rising): Foreign connections, spiritual solitude, budgeting, and lifestyle discipline.';
      default:
        return '';
    }
  };

  // Calculate live transit periods for all planets for the selected transit date
  const allTransitPeriods: Record<PlanetName, PlanetaryTransitPeriod> = useMemo(() => {
    return calculateAllPlanetaryTransitPeriods(effectiveTransitDate, nodeConvention);
  }, [effectiveTransitDate, nodeConvention]);

  return (
    <div className="space-y-6">
      {/* Top Interactive Time Selection Control Panel */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-astro-border pb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-astro-primary/10 border border-astro-primary/20 text-astro-primary flex items-center justify-center shadow-xs">
              <SlidersHorizontal className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-astro-primary font-serif">
                  {isHi ? 'कुंडली गोचर समय चयनकर्ता' : 'Vedic Kundali Gochar Time Travel'}
                </h2>
                <span className="text-[10px] uppercase font-bold text-white bg-astro-primary px-2.5 py-0.5 rounded-full shadow-xs">
                  {isHi ? 'सटीक ग्रह स्थिति' : 'Live Astronomical Engine'}
                </span>
                <span className="text-[10px] uppercase font-semibold text-astro-secondary-text bg-white border border-astro-border px-2 py-0.5 rounded-full shadow-2xs">
                  {isHi
                    ? `राहु/केतु: ${nodeConvention === 'true' ? 'वास्तविक पात (True Node)' : 'मध्यम पात (Mean Node)'}`
                    : `Rahu/Ketu: ${nodeConvention === 'true' ? 'True Node' : 'Mean Node'}`}
                </span>
              </div>
              <p className="text-xs text-astro-secondary-text mt-0.5">
                {isHi
                  ? `किसी भी विशिष्ट दिनांक एवं समय का ग्रह गोचर देखें | जन्म चंद्र: ${ZODIAC_HI[moonSignName] || moonSignName} | जन्म लग्न: ${ZODIAC_HI[lagnaSignName] || lagnaSignName}`
                  : `Calculate exact planetary transits for any specific date & time relative to Moon (${moonSignName}) & Lagna (${lagnaSignName})`}
              </p>
            </div>
          </div>

          {/* Current Selection Badge */}
          <div className="flex items-center gap-2 text-xs">
            <div className="px-3.5 py-2 rounded-xl bg-astro-card border border-astro-border text-right">
              <span className="text-astro-secondary-text text-[10px] block uppercase font-semibold">
                {isHi ? 'चयनित गोचर समय' : 'Selected Transit Time'}
              </span>
              <span className="font-bold text-astro-primary font-mono text-sm">
                {transitDateFormatted}
              </span>
            </div>
          </div>
        </div>

        {/* Date & Time Picker Controls */}
        <div className="pt-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Input */}
            <div className="flex items-center gap-2 bg-astro-card px-3 py-1.5 rounded-xl border border-astro-border">
              <Calendar className="w-4 h-4 text-astro-primary" />
              <label className="text-xs font-semibold text-astro-text">
                {isHi ? 'दिनांक:' : 'Date:'}
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => updateDateTime(e.target.value, selectedTime)}
                className="bg-white border border-astro-border rounded-lg px-2.5 py-1 text-xs font-bold text-astro-primary focus:outline-none focus:ring-2 focus:ring-astro-primary/20"
              />
            </div>

            {/* Time Input */}
            <div className="flex items-center gap-2 bg-astro-card px-3 py-1.5 rounded-xl border border-astro-border">
              <Clock className="w-4 h-4 text-astro-primary" />
              <label className="text-xs font-semibold text-astro-text">
                {isHi ? 'समय:' : 'Time:'}
              </label>
              <input
                type="time"
                value={selectedTime}
                onChange={(e) => updateDateTime(selectedDate, e.target.value)}
                className="bg-white border border-astro-border rounded-lg px-2.5 py-1 text-xs font-bold text-astro-primary focus:outline-none focus:ring-2 focus:ring-astro-primary/20"
              />
            </div>

            {/* Quick Reset to Current Time Button */}
            <button
              onClick={() => setPreset('now')}
              className="px-3 py-2 rounded-xl bg-astro-primary hover:bg-astro-primary/90 text-white text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs"
              title={isHi ? 'वर्तमान समय पर रीसेट करें' : 'Reset to Current Time'}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>{isHi ? 'आज / अभी (Now)' : 'Current Moment'}</span>
            </button>
          </div>

          {/* Quick Step Buttons */}
          <div className="flex items-center flex-wrap gap-1.5 text-xs">
            <span className="text-[11px] text-astro-secondary-text font-medium mr-1">
              {isHi ? 'त्वरित छलांग:' : 'Quick Jump:'}
            </span>
            <button
              onClick={() => setPreset('birth')}
              className="px-2.5 py-1 rounded-lg bg-astro-card hover:bg-astro-border border border-astro-border text-astro-text font-semibold text-[11px] transition-colors"
            >
              {isHi ? 'जन्म समय' : 'Birth Time'}
            </button>
            <button
              onClick={() => setPreset('minus1y')}
              className="px-2 py-1 rounded-lg bg-astro-card hover:bg-astro-border border border-astro-border text-astro-text text-[11px] transition-colors"
            >
              -1 {isHi ? 'वर्ष' : 'Yr'}
            </button>
            <button
              onClick={() => setPreset('minus1m')}
              className="px-2 py-1 rounded-lg bg-astro-card hover:bg-astro-border border border-astro-border text-astro-text text-[11px] transition-colors"
            >
              -1 {isHi ? 'माह' : 'Mo'}
            </button>
            <button
              onClick={() => setPreset('plus1m')}
              className="px-2 py-1 rounded-lg bg-astro-card hover:bg-astro-border border border-astro-border text-astro-text text-[11px] transition-colors"
            >
              +1 {isHi ? 'माह' : 'Mo'}
            </button>
            <button
              onClick={() => setPreset('plus6m')}
              className="px-2 py-1 rounded-lg bg-astro-card hover:bg-astro-border border border-astro-border text-astro-text text-[11px] transition-colors"
            >
              +6 {isHi ? 'माह' : 'Mo'}
            </button>
            <button
              onClick={() => setPreset('plus1y')}
              className="px-2 py-1 rounded-lg bg-astro-card hover:bg-astro-border border border-astro-border text-astro-text text-[11px] transition-colors"
            >
              +1 {isHi ? 'वर्ष' : 'Yr'}
            </button>
          </div>
        </div>

        {/* Sade Sati Alert Banner for selected time */}
        <div className="mt-4 pt-3 border-t border-astro-border">
          {sadeSatiPhase !== 'none' ? (
            <div className="bg-astro-accent/10 border border-astro-accent/30 rounded-xl p-3.5 flex items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2.5">
                <AlertBadge 
                  id={`sadesati-${sadeSatiPhase}-${kundali.birthDetails.name}`}
                  label={isHi ? `साढ़ेसाती सक्रिय (${transitDateFormatted})` : `Shani Sade Sati Active (${transitDateFormatted})`}
                  type="warning"
                />
                <div>
                  <p className="text-astro-secondary-text text-[11px] mt-0.5">
                    {sadeSatiPhase === 'rising' &&
                      (isHi ? `प्रथम चरण (उदय मान) - शनि चंद्र से 12वें भाव (${saturnTransit.sign}) में स्थित है।` : `1st Phase (Rising) - Saturn in 12th from Moon (${saturnTransit.sign}).`)}
                    {sadeSatiPhase === 'peak' &&
                      (isHi ? `द्वितीय चरण (शिखर काल) - शनि जन्म चंद्र राशि (${saturnTransit.sign}) पर गोचर कर रहा है।` : `2nd Phase (Peak) - Saturn transiting directly over Natal Moon (${saturnTransit.sign}).`)}
                    {sadeSatiPhase === 'setting' &&
                      (isHi ? `तृतीय चरण (अस्त मान) - शनि चंद्र से द्वितीय भाव (${saturnTransit.sign}) में स्थित है।` : `3rd Phase (Setting) - Saturn in 2nd from Moon (${saturnTransit.sign}).`)}
                  </p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-lg bg-astro-accent/20 text-astro-primary font-bold text-[10px] uppercase border border-astro-accent/40 shrink-0">
                {sadeSatiPhase.toUpperCase()} PHASE
              </span>
            </div>
          ) : isDhaiya ? (
            <div className="bg-[#6F9690]/15 border border-[#6F9690]/30 rounded-xl p-3.5 flex items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2.5">
                <AlertBadge 
                  id={`dhaiya-${saturnHouseFromMoon}-${kundali.birthDetails.name}`}
                  label={isHi ? 'शनि की ढैय्या सक्रिय' : 'Shani Dhaiya Active'}
                  type="warning"
                />
                <div>
                  <p className="text-astro-secondary-text text-[11px] mt-0.5">
                    {isHi
                      ? `शनि चंद्र से ${saturnHouseFromMoon}वें भाव (${saturnTransit.sign}) में गोचर कर रहा है।`
                      : `Saturn transiting in ${formatOrdinal(saturnHouseFromMoon)} house (${saturnTransit.sign}) from Natal Moon.`}
                  </p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-lg bg-[#6F9690]/20 text-astro-primary font-bold text-[10px] uppercase border border-[#6F9690]/40 shrink-0">
                DHAIYA ACTIVE
              </span>
            </div>
          ) : (
            <div className="bg-astro-primary/10 border border-astro-primary/20 rounded-xl p-3.5 flex items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2.5">
                <CheckCircle className="w-5 h-5 text-astro-primary shrink-0" />
                <div>
                  <span className="font-bold text-astro-primary">
                    {isHi ? `साढ़ेसाती एवं ढैय्या से मुक्त (${transitDateFormatted})` : `Free from Sade Sati & Dhaiya (${transitDateFormatted})`}
                  </span>
                  <p className="text-astro-secondary-text text-[11px] mt-0.5">
                    {isHi
                      ? `चयनित समय पर शनि गोचर (${saturnTransit.sign}) आपकी चंद्र राशि के सापेक्ष अनुकूल/तटस्थ भाव (भाव ${saturnHouseFromMoon}) में है।`
                      : `At the selected time, Saturn in ${saturnTransit.sign} is in a favorable/neutral house (House ${saturnHouseFromMoon}) relative to Moon.`}
                  </p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-lg bg-astro-primary/15 text-astro-primary font-bold text-[10px] uppercase border border-astro-primary/30 shrink-0">
                CLEAR
              </span>
            </div>
          )}
        </div>
      </div>

      {/* PERSPECTIVE SELECTOR & TRI-LAGNA TOGGLE */}
      <div className="bg-astro-card border border-astro-border rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Compass className="w-4 h-4 text-astro-primary" />
          <span className="text-xs font-bold text-astro-primary uppercase tracking-wider">
            {isHi ? 'गोचर दृष्टिकोण (Perspective Lenses):' : 'Gochar Perspective Lenses:'}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => setSelectedPerspective('lagna')}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
              selectedPerspective === 'lagna'
                ? 'bg-astro-primary text-white shadow-xs font-bold'
                : 'bg-astro-bg text-astro-secondary-text hover:text-astro-primary border border-astro-border'
            }`}
          >
            {isHi ? `जन्म लग्न (${lagnaSignName.slice(0, 3)})` : `Janma Lagna (${lagnaSignName.slice(0, 3)})`}
          </button>
          <button
            onClick={() => setSelectedPerspective('moon')}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
              selectedPerspective === 'moon'
                ? 'bg-astro-primary text-white shadow-xs font-bold'
                : 'bg-astro-bg text-astro-secondary-text hover:text-astro-primary border border-astro-border'
            }`}
          >
            {isHi ? `चंद्र लग्न (${moonSignName.slice(0, 3)})` : `Chandra Lagna (${moonSignName.slice(0, 3)})`}
          </button>
          <button
            onClick={() => setSelectedPerspective('sun')}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
              selectedPerspective === 'sun'
                ? 'bg-astro-primary text-white shadow-xs font-bold'
                : 'bg-astro-bg text-astro-secondary-text hover:text-astro-primary border border-astro-border'
            }`}
          >
            {isHi ? `सूर्य लग्न (${sunSignName.slice(0, 3)})` : `Surya Lagna (${sunSignName.slice(0, 3)})`}
          </button>
          <button
            onClick={() => setSelectedPerspective('tri_lagna')}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
              selectedPerspective === 'tri_lagna'
                ? 'bg-astro-primary text-white shadow-xs font-bold'
                : 'bg-astro-bg text-astro-secondary-text hover:text-astro-primary border border-astro-border'
            }`}
          >
            {isHi ? 'त्रि-लग्न सारिणी (Tri-Lagna)' : 'Tri-Lagna Matrix'}
          </button>
        </div>
      </div>

      {/* TRI-LAGNA EXPANDABLE COMPARATIVE MATRIX */}
      {selectedPerspective === 'tri_lagna' && (
        <div className="bg-astro-bg border border-astro-border rounded-xl p-4 shadow-sm space-y-3">
          <div className="flex items-center justify-between border-b border-astro-border pb-2">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-astro-primary" />
              <h4 className="font-bold text-astro-primary font-serif text-sm">
                {isHi ? 'त्रि-लग्न गोचर तुलनात्मक सारिणी (Lagna • Moon • Sun)' : 'Tri-Lagna Comparative Transit Matrix (Ascendant • Moon • Sun)'}
              </h4>
            </div>
            <span className="text-[11px] text-astro-secondary-text font-mono">
              Lagna: {lagnaSignName} • Moon: {moonSignName} • Sun: {sunSignName}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-9 gap-2">
            {dynamicEphemeris.map(tp => {
              const hLagna = calculateHouseDistance(lagnaSignIndex, tp.signIndex);
              const hMoon = calculateHouseDistance(moonSignIndex, tp.signIndex);
              const hSun = calculateHouseDistance(sunSignIndex, tp.signIndex);
              const isSelected = selectedTransitPlanet === tp.planet;

              return (
                <div
                  key={tp.planet}
                  onClick={() => setSelectedTransitPlanet(tp.planet)}
                  className={`p-2.5 rounded-xl border transition-all cursor-pointer ${
                    isSelected
                      ? 'bg-amber-500/10 border-amber-500/40 ring-1 ring-amber-500/40'
                      : 'bg-astro-card border-astro-border hover:border-astro-primary/40'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-astro-primary text-xs">{tp.planet}</span>
                    <span className="text-[10px] font-mono text-astro-secondary-text">{tp.sign.slice(0, 3)}</span>
                  </div>
                  <div className="text-[10px] space-y-0.5 mt-1 pt-1 border-t border-astro-border text-astro-secondary-text">
                    <div>Lagna: <strong className="text-astro-text">H{hLagna}</strong></div>
                    <div>Moon: <strong className="text-astro-text">H{hMoon}</strong></div>
                    <div>Sun: <strong className="text-astro-text">H{hSun}</strong></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* DUAL COMPARATIVE CHARTS: Natal vs Selected Time Gochar */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-astro-primary" />
            <h3 className="font-bold text-astro-primary font-serif text-lg">
              {isHi ? 'तुलनात्मक कुंडली: जन्म लग्न बनाम चयनित गोचर' : 'Comparative Dual View: Natal vs Selected Time Gochar'}
            </h3>
          </div>

          {/* Chart Layout and Style Switcher */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Layout Mode (Side-by-Side Grid vs 1-Chart Transit vs 1-Chart Natal vs Stacked Full-Width) */}
            <div className="flex items-center gap-1 bg-astro-card p-1 rounded-xl border border-astro-border text-xs flex-wrap">
              <button
                onClick={() => setLayoutMode('grid')}
                className={`px-2.5 py-0.5 rounded-md font-semibold transition-all ${
                  layoutMode === 'grid' ? 'bg-astro-primary text-white shadow-xs font-bold' : 'text-astro-secondary-text hover:text-astro-primary'
                }`}
                title={isHi ? 'दोहरी कुंडली (साथ-साथ)' : 'Side-by-side 2 Columns'}
              >
                {isHi ? '२-कॉलम (ग्रिड)' : 'Dual (2-Col)'}
              </button>
              <button
                onClick={() => setLayoutMode('transit')}
                className={`px-2.5 py-0.5 rounded-md font-semibold transition-all ${
                  layoutMode === 'transit' ? 'bg-astro-primary text-white shadow-xs font-bold' : 'text-astro-secondary-text hover:text-astro-primary'
                }`}
                title={isHi ? '१ कुंडली दृश्य: केवल लाइव गोचर' : '1 Chart View: Live Gochar with Period'}
              >
                {isHi ? '१ चक्र (गोचर काल)' : '1 Chart (Gochar)'}
              </button>
              <button
                onClick={() => setLayoutMode('natal')}
                className={`px-2.5 py-0.5 rounded-md font-semibold transition-all ${
                  layoutMode === 'natal' ? 'bg-astro-primary text-white shadow-xs font-bold' : 'text-astro-secondary-text hover:text-astro-primary'
                }`}
                title={isHi ? '१ कुंडली दृश्य: केवल जन्म लग्न (D1)' : '1 Chart View: Natal D1 Lagna'}
              >
                {isHi ? '१ चक्र (जन्म D1)' : '1 Chart (Natal D1)'}
              </button>
              <button
                onClick={() => setLayoutMode('stacked')}
                className={`px-2.5 py-0.5 rounded-md font-semibold transition-all ${
                  layoutMode === 'stacked' ? 'bg-astro-primary text-white shadow-xs font-bold' : 'text-astro-secondary-text hover:text-astro-primary'
                }`}
                title={isHi ? 'बड़ा आकार (विस्तृत पूर्ण चौड़ाई)' : 'Stacked Full Width Max Size'}
              >
                {isHi ? 'पूर्ण चौड़ाई' : 'Full Width'}
              </button>
            </div>

            {/* Scale / Zoom Control Buttons */}
            <div className="flex items-center gap-1 bg-astro-card p-1 rounded-xl border border-astro-border text-xs">
              <button
                onClick={() => setChartScale(prev => Math.max(85, prev - 15))}
                className="p-1 rounded-md text-astro-secondary-text hover:text-astro-primary hover:bg-astro-border/50 transition-colors"
                title={isHi ? 'छोटा करें' : 'Zoom Out'}
                disabled={chartScale <= 85}
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <span className="px-1.5 font-mono text-[11px] font-bold text-astro-primary">
                {chartScale}%
              </span>
              <button
                onClick={() => setChartScale(prev => Math.min(145, prev + 15))}
                className="p-1 rounded-md text-astro-secondary-text hover:text-astro-primary hover:bg-astro-border/50 transition-colors"
                title={isHi ? 'बड़ा करें' : 'Zoom In'}
                disabled={chartScale >= 145}
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
            </div>

            <button
              onClick={() => setShowDegrees(!showDegrees)}
              className={`px-2.5 py-1 rounded-lg border text-xs font-semibold transition-colors ${
                showDegrees
                  ? 'bg-astro-primary/10 text-astro-primary border-astro-primary/30 font-bold'
                  : 'bg-astro-card text-astro-secondary-text border-astro-border'
              }`}
            >
              {showDegrees ? (isHi ? 'अंश: चालू' : 'Degrees: ON') : (isHi ? 'अंश: बंद' : 'Degrees: OFF')}
            </button>

            <div className="flex items-center gap-1 bg-astro-card p-1 rounded-xl border border-astro-border text-xs">
              <button
                onClick={() => setChartStyle('north')}
                className={`px-2.5 py-0.5 rounded-md font-semibold transition-all ${
                  chartStyle === 'north' ? 'bg-astro-primary text-white shadow-xs font-bold' : 'text-astro-secondary-text'
                }`}
              >
                {isHi ? 'उत्तर' : 'North'}
              </button>
              <button
                onClick={() => setChartStyle('south')}
                className={`px-2.5 py-0.5 rounded-md font-semibold transition-all ${
                  chartStyle === 'south' ? 'bg-astro-primary text-white shadow-xs font-bold' : 'text-astro-secondary-text'
                }`}
              >
                {isHi ? 'दक्षिण' : 'South'}
              </button>
              <button
                onClick={() => setChartStyle('east')}
                className={`px-2.5 py-0.5 rounded-md font-semibold transition-all ${
                  chartStyle === 'east' ? 'bg-astro-primary text-white shadow-xs font-bold' : 'text-astro-secondary-text'
                }`}
              >
                {isHi ? 'पूर्व' : 'East'}
              </button>
            </div>
          </div>
        </div>

        <GocharDualChart
          kundali={kundali}
          transitPlanets={transitPlanetsForChart}
          transitDateFormatted={transitDateFormatted}
          transitDateObj={effectiveTransitDate}
          chartStyle={chartStyle}
          layoutMode={layoutMode}
          chartScale={chartScale}
          lang={lang}
          showDegrees={showDegrees}
          selectedTransitPlanet={selectedTransitPlanet}
          onSelectTransitPlanet={(p) => setSelectedTransitPlanet(p)}
          highlightedHouses={selectedTransitCrossHighlight ? [selectedTransitCrossHighlight.houseFromLagna] : []}
          highlightedAspectHouses={selectedTransitCrossHighlight ? selectedTransitCrossHighlight.aspectHouses : []}
          affectedNatalPlanets={selectedTransitCrossHighlight ? selectedTransitCrossHighlight.contactedNatalPlanets : []}
        />
      </div>

      {/* 9 x 12 CLASSICAL GOCHAR DRISHTI & SAMBANDHA MATRIX (P2-A) */}
      <GocharDrishtiMatrix
        kundali={kundali}
        transitPlanets={transitPlanetsForChart}
        selectedPerspective={selectedPerspective === 'tri_lagna' ? 'lagna' : selectedPerspective}
        onSelectPerspective={(p) => setSelectedPerspective(p)}
        selectedTransitPlanet={selectedTransitPlanet}
        onSelectTransitPlanet={(p) => setSelectedTransitPlanet(p)}
        lang={lang}
        onOpenEvidenceDrawer={(planet) => {
          setShowEvidenceDrawer(true);
          if (planet) setEvidenceFocusPlanet(planet);
        }}
      />

      {/* 6-PART EVIDENCE & PROVENANCE DRAWER CTA */}
      <div className="bg-[#174C4B]/5 border border-[#B58A4A]/30 rounded-xl p-3.5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-stone-800 dark:text-stone-200">
          <FileCheck2 className="w-4 h-4 text-[#B58A4A]" />
          <div>
            <span className="font-bold block text-sm text-[#174C4B]">
              {isHi ? 'शास्त्रीय प्रमाण एवं स्रोत विवरण (6-Part Evidence)' : 'Classical Jyotish Evidence & Epistemic Provenance'}
            </span>
            <span className="text-[11px] text-stone-500">
              {isHi ? 'गणना तथ्य • शास्त्रीय नियम • फलित • अनुमान • अटकल • अनिश्चितता' : 'Calculated Fact • Classical Rule • Interpretation • Inference • Speculation • Uncertainty'}
            </span>
          </div>
        </div>
        <button
          onClick={() => setShowEvidenceDrawer(!showEvidenceDrawer)}
          className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#174C4B] hover:bg-[#174C4B]/90 text-white text-xs font-bold transition-all shadow-xs"
        >
          <FileCheck2 className="w-3.5 h-3.5" />
          <span>{showEvidenceDrawer ? (isHi ? 'प्रमाण विवरण छुपाएं' : 'Hide Evidence Drawer') : (isHi ? 'प्रमाण विवरण देखें' : 'View Evidence & Provenance')}</span>
          {showEvidenceDrawer ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* INLINE EVIDENCE PROVENANCE DRAWER */}
      {showEvidenceDrawer && (
        <EvidenceProvenanceDrawer
          kundali={kundali}
          lang={lang}
          activeMD={activeMD}
          activeAD={activeAD}
          targetAnalysisDateTime={effectiveTransitDate}
        />
      )}

      {/* UNIFIED GOCHAR TEMPORAL TIMELINE (P2-B) */}
      <UnifiedGocharTimeline
        kundali={kundali}
        targetAnalysisDateTime={effectiveTransitDate}
        onTargetAnalysisDateTimeChange={(d) => {
          updateDateTime(formatDateForInput(d), formatTimeForInput(d));
        }}
        selectedTransitPlanet={selectedTransitPlanet}
        onSelectTransitPlanet={(p) => setSelectedTransitPlanet(p)}
        selectedPerspective={selectedPerspective === 'tri_lagna' ? 'lagna' : selectedPerspective}
        onSelectPerspective={(p) => setSelectedPerspective(p)}
        onOpenEvidenceDrawer={(planet) => {
          setShowEvidenceDrawer(true);
          if (planet) setEvidenceFocusPlanet(planet);
        }}
        lang={lang}
      />

      {/* Main Focus: Jupiter & Saturn Transits Dual Cards for Selected Time */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* JUPITER (GURU) GOCHAR CARD */}
        {(() => {
          const jupiterPeriod = allTransitPeriods['Jupiter'];
          const jupHouseSun = calculateHouseDistance(sunSignIndex, jupiterTransit.signIndex);
          const jupVedha = allVedhaStatus['Jupiter'];
          const isSelected = selectedTransitPlanet === 'Jupiter';

          return (
            <div 
              onClick={() => setSelectedTransitPlanet('Jupiter')}
              className={`bg-astro-bg border rounded-2xl p-5 shadow-sm flex flex-col justify-between space-y-4 transition-all cursor-pointer ${
                isSelected ? 'border-amber-500 ring-2 ring-amber-500/20 shadow-md' : 'border-astro-border hover:border-astro-primary/40'
              }`}
            >
              <div>
                <div className="flex items-center justify-between border-b border-astro-border pb-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-astro-accent/15 border border-astro-accent/30 text-astro-primary flex items-center justify-center font-bold text-lg font-serif">
                      गु
                    </div>
                    <div>
                      <h3 className="font-bold text-astro-text font-serif text-base flex items-center gap-2">
                        <span>{isHi ? 'बृहस्पति (गुरु) गोचर स्थिति' : 'Jupiter (Guru) Transit Status'}</span>
                        {isSelected && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-800 dark:text-amber-200 border border-amber-500/40 font-bold">
                            {isHi ? 'चार्ट पर केंद्रित' : 'Focused on Chart'}
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-astro-secondary-text">
                        {isHi ? 'ज्ञान, धर्म, संतति एवं भाग्य कारक' : 'Wisdom, Prosperity, Marriage & Fortune'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {isJupiterAuspicious ? (
                      <span className="px-2.5 py-1 rounded-lg bg-astro-primary/10 text-astro-primary border border-astro-primary/30 text-[10px] font-bold uppercase flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-astro-accent" />
                        {isHi ? 'शुभ गोचर' : 'Auspicious'}
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 rounded-lg bg-astro-accent/15 text-astro-primary border border-astro-accent/30 text-[10px] font-bold uppercase flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        {isHi ? 'मध्यम गोचर' : 'Neutral / Growth'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Current Position Metrics with Lagna, Moon, and Sun (Tri-Lagna) */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3.5 text-xs">
                  <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
                    <span className="text-astro-secondary-text block text-[9.5px] uppercase font-semibold">
                      {isHi ? 'वर्तमान गोचर राशि' : 'Current Sign'}
                    </span>
                    <span className="font-bold text-astro-primary text-xs">
                      {isHi ? ZODIAC_HI[jupiterTransit.sign] || jupiterTransit.sign : jupiterTransit.sign}
                    </span>
                    <span className="text-[10px] text-astro-secondary-text block font-mono">
                      {jupiterTransit.formattedDegree} {jupiterTransit.isRetrograde ? '(R)' : ''}
                    </span>
                  </div>
                  <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
                    <span className="text-astro-secondary-text block text-[9.5px] uppercase font-semibold">
                      {isHi ? 'लग्न से भाव' : 'From Lagna'}
                    </span>
                    <span className="font-bold text-astro-text text-xs">
                      H{jupiterHouseFromLagna} <span className="font-normal text-[10px] text-astro-secondary-text">({lagnaSignName.slice(0, 3)})</span>
                    </span>
                  </div>
                  <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
                    <span className="text-astro-secondary-text block text-[9.5px] uppercase font-semibold">
                      {isHi ? 'चंद्र से भाव' : 'From Moon'}
                    </span>
                    <span className="font-bold text-astro-primary text-xs">
                      H{jupiterHouseFromMoon} <span className="font-normal text-[10px] text-astro-secondary-text">({moonSignName.slice(0, 3)})</span>
                    </span>
                  </div>
                  <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
                    <span className="text-astro-secondary-text block text-[9.5px] uppercase font-semibold">
                      {isHi ? 'सूर्य से भाव' : 'From Sun'}
                    </span>
                    <span className="font-bold text-astro-text text-xs">
                      H{jupHouseSun} <span className="font-normal text-[10px] text-astro-secondary-text">({sunSignName.slice(0, 3)})</span>
                    </span>
                  </div>
                </div>

                {/* Classical Vedha (Obstruction) Status Banner */}
                {jupVedha && (
                  <div className={`p-2.5 rounded-xl border text-xs mb-3.5 space-y-1 ${
                    jupVedha.hasVedha
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-900 dark:text-amber-200'
                      : jupVedha.isFavorableFromMoon
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-900 dark:text-emerald-200'
                      : 'bg-astro-card border-astro-border text-astro-secondary-text'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 font-bold">
                        {jupVedha.hasVedha ? (
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        ) : (
                          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        )}
                        <span className="text-[11px] uppercase tracking-wider">
                          {isHi ? 'शास्त्रीय गोचर वेध (Phaladeepika Ch. 26)' : 'Classical Vedha Status (Phaladeepika 26)'}
                        </span>
                      </div>
                      <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-white/60 dark:bg-black/20 font-semibold">
                        {jupVedha.factualStatus}
                      </span>
                    </div>
                    <p className="text-[11px] leading-tight">
                      {isHi ? jupVedha.interpretationHi : jupVedha.interpretationEn}
                    </p>
                  </div>
                )}

                {/* Upcoming Rashi & Ingress Date Prominent Box */}
                {jupiterPeriod && (
                  <div className="p-3 bg-white/80 dark:bg-astro-card/80 rounded-xl border border-astro-primary/30 shadow-2xs space-y-2 mb-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                      <div className="flex items-center gap-1.5 font-bold text-astro-primary">
                        <Calendar className="w-3.5 h-3.5 text-astro-primary" />
                        <span>{isHi ? 'अगली राशि एवं काल अवधि (Next Period)' : 'Upcoming Rashi & Next Period'}</span>
                      </div>
                      <span className="font-mono text-[10px] font-bold text-astro-primary bg-astro-primary/10 px-2 py-0.5 rounded border border-astro-primary/20">
                        {isHi ? `${jupiterPeriod.daysUntilNextIngress} दिन शेष` : `${jupiterPeriod.daysUntilNextIngress} days remaining`}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-astro-bg rounded-lg border border-astro-border">
                        <span className="text-[9.5px] text-astro-secondary-text uppercase font-semibold block">
                          {isHi ? 'आगामी राशि (Next Rashi)' : 'Next Zodiac Sign'}
                        </span>
                        <span className="font-bold text-astro-primary text-xs">
                          {jupiterPeriod.nextSign} <span className="font-normal text-astro-secondary-text">({ZODIAC_HI[jupiterPeriod.nextSign] || ''})</span>
                        </span>
                      </div>
                      <div className="p-2 bg-astro-bg rounded-lg border border-astro-border">
                        <span className="text-[9.5px] text-astro-secondary-text uppercase font-semibold block">
                          {isHi ? 'अगली गोचर अवधि (From - To)' : 'Next Period (From - To)'}
                        </span>
                        <span className="font-bold text-astro-text text-xs font-mono block">
                          {jupiterPeriod.nextPeriodStartDateFormatted} → {jupiterPeriod.nextPeriodEndDateFormatted}
                        </span>
                        <span className="text-[9.5px] text-astro-secondary-text block mt-0.5">
                          {isHi ? `कुल: ${jupiterPeriod.nextDurationFormatted}` : `Duration: ${jupiterPeriod.nextDurationFormatted}`}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-1 pt-0.5">
                      <div className="flex items-center justify-between text-[10px] text-astro-secondary-text">
                        <span>{isHi ? 'वर्तमान गोचर काल' : 'Current Period'}: {jupiterPeriod.startDateFormatted} → {jupiterPeriod.endDateFormatted}</span>
                        <span className="font-mono font-bold text-astro-primary">{jupiterPeriod.progressPercentage}%</span>
                      </div>
                      <div className="w-full bg-astro-border/50 h-1.5 rounded-full overflow-hidden">
                        <div
                          className="bg-astro-primary h-full rounded-full transition-all duration-300"
                          style={{ width: `${Math.min(100, Math.max(0, jupiterPeriod.progressPercentage))}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Detailed Interpretation */}
                <div className="bg-astro-card border border-astro-border rounded-xl p-3.5 text-xs text-astro-text leading-relaxed mb-4">
                  <div className="font-bold text-astro-primary mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <ArrowUpRight className="w-3.5 h-3.5 text-astro-accent" />
                      <span>{isHi ? 'गुरु गोचर प्रभाव:' : 'Jupiter Transit Impact:'}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEvidenceDrawer(true);
                        setEvidenceFocusPlanet('Jupiter');
                      }}
                      className="flex items-center gap-1 text-[10px] font-semibold text-[#174C4B] hover:text-[#B58A4A] transition-colors"
                    >
                      <FileCheck2 className="w-3 h-3" />
                      <span>{isHi ? 'प्रमाण देखें' : 'View Evidence'}</span>
                    </button>
                  </div>
                  <p>{getJupiterHouseText(jupiterHouseFromMoon)}</p>
                </div>
              </div>

              {/* Jupiter Remedies */}
              <div className="border-t border-astro-border pt-3 text-[11px] text-astro-secondary-text">
                <span className="font-bold text-astro-primary block mb-1">
                  {isHi ? 'गुरु देव की प्रसन्नता के उपाय:' : 'Recommended Remedies for Jupiter:'}
                </span>
                <p className="italic">
                  {isHi
                    ? '• "ॐ बृं बृहस्पतये नमः" का नियमित जाप करें। गुरुवार को चने की दाल या बेसन के लड्डू दान करें।'
                    : '• Chanting "Om Gram Greem Grom Sah Gurave Namah", donating yellow grains on Thursdays, respecting elders.'}
                </p>
              </div>
            </div>
          );
        })()}

        {/* SATURN (SHANI) GOCHAR CARD */}
        {(() => {
          const saturnPeriod = allTransitPeriods['Saturn'];
          const satHouseSun = calculateHouseDistance(sunSignIndex, saturnTransit.signIndex);
          const satVedha = allVedhaStatus['Saturn'];
          const isSelected = selectedTransitPlanet === 'Saturn';

          return (
            <div 
              onClick={() => setSelectedTransitPlanet('Saturn')}
              className={`bg-astro-bg border rounded-2xl p-5 shadow-sm flex flex-col justify-between space-y-4 transition-all cursor-pointer ${
                isSelected ? 'border-amber-500 ring-2 ring-amber-500/20 shadow-md' : 'border-astro-border hover:border-astro-primary/40'
              }`}
            >
              <div>
                <div className="flex items-center justify-between border-b border-astro-border pb-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#6F9690]/15 border border-[#6F9690]/30 text-astro-primary flex items-center justify-center font-bold text-lg font-serif">
                      श
                    </div>
                    <div>
                      <h3 className="font-bold text-astro-text font-serif text-base flex items-center gap-2">
                        <span>{isHi ? 'शनि (शनैश्चर) गोचर स्थिति' : 'Saturn (Shani) Transit Status'}</span>
                        {isSelected && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-800 dark:text-amber-200 border border-amber-500/40 font-bold">
                            {isHi ? 'चार्ट पर केंद्रित' : 'Focused on Chart'}
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-astro-secondary-text">
                        {isHi ? 'कर्म, अनुशासन, न्याय एवं आयु कारक' : 'Karma, Discipline, Career & Justice'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {isSaturnAuspicious ? (
                      <span className="px-2.5 py-1 rounded-lg bg-astro-primary/10 text-astro-primary border border-astro-primary/30 text-[10px] font-bold uppercase flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-astro-accent" />
                        {isHi ? 'शुभ गोचर' : 'Auspicious'}
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 rounded-lg bg-[#6F9690]/15 text-astro-primary border border-[#6F9690]/30 text-[10px] font-bold uppercase flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        {isHi ? 'अनुशासन काल' : 'Disciplined'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Current Position Metrics with Lagna, Moon, and Sun (Tri-Lagna) */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3.5 text-xs">
                  <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
                    <span className="text-astro-secondary-text block text-[9.5px] uppercase font-semibold">
                      {isHi ? 'वर्तमान गोचर राशि' : 'Current Sign'}
                    </span>
                    <span className="font-bold text-astro-primary text-xs">
                      {isHi ? ZODIAC_HI[saturnTransit.sign] || saturnTransit.sign : saturnTransit.sign}
                    </span>
                    <span className="text-[10px] text-astro-secondary-text block font-mono">
                      {saturnTransit.formattedDegree} {saturnTransit.isRetrograde ? '(R)' : ''}
                    </span>
                  </div>
                  <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
                    <span className="text-astro-secondary-text block text-[9.5px] uppercase font-semibold">
                      {isHi ? 'लग्न से भाव' : 'From Lagna'}
                    </span>
                    <span className="font-bold text-astro-text text-xs">
                      H{saturnHouseFromLagna} <span className="font-normal text-[10px] text-astro-secondary-text">({lagnaSignName.slice(0, 3)})</span>
                    </span>
                  </div>
                  <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
                    <span className="text-astro-secondary-text block text-[9.5px] uppercase font-semibold">
                      {isHi ? 'चंद्र से भाव' : 'From Moon'}
                    </span>
                    <span className="font-bold text-astro-primary text-xs">
                      H{saturnHouseFromMoon} <span className="font-normal text-[10px] text-astro-secondary-text">({moonSignName.slice(0, 3)})</span>
                    </span>
                  </div>
                  <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
                    <span className="text-astro-secondary-text block text-[9.5px] uppercase font-semibold">
                      {isHi ? 'सूर्य से भाव' : 'From Sun'}
                    </span>
                    <span className="font-bold text-astro-text text-xs">
                      H{satHouseSun} <span className="font-normal text-[10px] text-astro-secondary-text">({sunSignName.slice(0, 3)})</span>
                    </span>
                  </div>
                </div>

                {/* Classical Vedha (Obstruction) Status Banner */}
                {satVedha && (
                  <div className={`p-2.5 rounded-xl border text-xs mb-3.5 space-y-1 ${
                    satVedha.hasVedha
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-900 dark:text-amber-200'
                      : satVedha.isFavorableFromMoon
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-900 dark:text-emerald-200'
                      : 'bg-astro-card border-astro-border text-astro-secondary-text'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 font-bold">
                        {satVedha.hasVedha ? (
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        ) : (
                          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        )}
                        <span className="text-[11px] uppercase tracking-wider">
                          {isHi ? 'शास्त्रीय गोचर वेध (Phaladeepika Ch. 26)' : 'Classical Vedha Status (Phaladeepika 26)'}
                        </span>
                      </div>
                      <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-white/60 dark:bg-black/20 font-semibold">
                        {satVedha.factualStatus}
                      </span>
                    </div>
                    <p className="text-[11px] leading-tight">
                      {isHi ? satVedha.interpretationHi : satVedha.interpretationEn}
                    </p>
                  </div>
                )}

                {/* Upcoming Rashi & Next Period Prominent Box */}
                {saturnPeriod && (
                  <div className="p-3 bg-white/80 dark:bg-astro-card/80 rounded-xl border border-astro-primary/30 shadow-2xs space-y-2 mb-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                      <div className="flex items-center gap-1.5 font-bold text-astro-primary">
                        <Calendar className="w-3.5 h-3.5 text-astro-primary" />
                        <span>{isHi ? 'अगली राशि एवं काल अवधि (Next Period)' : 'Upcoming Rashi & Next Period'}</span>
                      </div>
                      <span className="font-mono text-[10px] font-bold text-astro-primary bg-astro-primary/10 px-2 py-0.5 rounded border border-astro-primary/20">
                        {isHi ? `${saturnPeriod.daysUntilNextIngress} दिन शेष` : `${saturnPeriod.daysUntilNextIngress} days remaining`}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-astro-bg rounded-lg border border-astro-border">
                        <span className="text-[9.5px] text-astro-secondary-text uppercase font-semibold block">
                          {isHi ? 'आगामी राशि (Next Rashi)' : 'Next Zodiac Sign'}
                        </span>
                        <span className="font-bold text-astro-primary text-xs">
                          {saturnPeriod.nextSign} <span className="font-normal text-astro-secondary-text">({ZODIAC_HI[saturnPeriod.nextSign] || ''})</span>
                        </span>
                      </div>
                      <div className="p-2 bg-astro-bg rounded-lg border border-astro-border">
                        <span className="text-[9.5px] text-astro-secondary-text uppercase font-semibold block">
                          {isHi ? 'अगली गोचर अवधि (From - To)' : 'Next Period (From - To)'}
                        </span>
                        <span className="font-bold text-astro-text text-xs font-mono block">
                          {saturnPeriod.nextPeriodStartDateFormatted} → {saturnPeriod.nextPeriodEndDateFormatted}
                        </span>
                        <span className="text-[9.5px] text-astro-secondary-text block mt-0.5">
                          {isHi ? `कुल: ${saturnPeriod.nextDurationFormatted}` : `Duration: ${saturnPeriod.nextDurationFormatted}`}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-1 pt-0.5">
                      <div className="flex items-center justify-between text-[10px] text-astro-secondary-text">
                        <span>{isHi ? 'वर्तमान गोचर काल' : 'Current Period'}: {saturnPeriod.startDateFormatted} → {saturnPeriod.endDateFormatted}</span>
                        <span className="font-mono font-bold text-astro-primary">{saturnPeriod.progressPercentage}%</span>
                      </div>
                      <div className="w-full bg-astro-border/50 h-1.5 rounded-full overflow-hidden">
                        <div
                          className="bg-astro-primary h-full rounded-full transition-all duration-300"
                          style={{ width: `${Math.min(100, Math.max(0, saturnPeriod.progressPercentage))}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Detailed Interpretation */}
                <div className="bg-astro-card border border-astro-border rounded-xl p-3.5 text-xs text-astro-text leading-relaxed mb-4">
                  <div className="font-bold text-astro-primary mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <ArrowUpRight className="w-3.5 h-3.5 text-astro-accent" />
                      <span>{isHi ? 'शनि गोचर प्रभाव:' : 'Saturn Transit Impact:'}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEvidenceDrawer(true);
                        setEvidenceFocusPlanet('Saturn');
                      }}
                      className="flex items-center gap-1 text-[10px] font-semibold text-[#174C4B] hover:text-[#B58A4A] transition-colors"
                    >
                      <FileCheck2 className="w-3 h-3" />
                      <span>{isHi ? 'प्रमाण देखें' : 'View Evidence'}</span>
                    </button>
                  </div>
                  <p>{getSaturnHouseText(saturnHouseFromMoon)}</p>
                </div>
              </div>

              {/* Saturn Remedies */}
              <div className="border-t border-astro-border pt-3 text-[11px] text-astro-secondary-text">
                <span className="font-bold text-astro-primary block mb-1">
                  {isHi ? 'शनि देव की कृपा के उपाय:' : 'Recommended Remedies for Saturn:'}
                </span>
                <p className="italic">
                  {isHi
                    ? '• प्रत्येक शनिवार हनुमान चालीसा का पाठ करें। सरसों के तेल का दीपक पीपल के वृक्ष के नीचे जलाएं।'
                    : '• Recite Hanuman Chalisa on Saturdays, light a mustard oil lamp under a Peepal tree, help the needy.'}
                </p>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Complete All Grahas Ephemeris Table for Selected Time with Surya Lagna and Vedha */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 border-b border-astro-border pb-3">
          <div className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-astro-primary" />
            <h3 className="font-bold text-astro-primary font-serif text-lg">
              {isHi ? `सर्वे ग्रह गोचर तालिका (${transitDateFormatted})` : `Complete Planetary Transits Matrix (${transitDateFormatted})`}
            </h3>
          </div>
          <span className="text-xs text-astro-secondary-text">
            {isHi ? 'पंक्ति पर क्लिक करके चार्ट पर ग्रह केंद्रित करें' : 'Click row to cross-highlight planet on chart'}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-astro-border text-astro-secondary-text uppercase text-[10px] tracking-wider font-semibold bg-astro-card">
                <th className="py-3 px-3">{isHi ? 'ग्रह' : 'Planet'}</th>
                <th className="py-3 px-3">{isHi ? 'गोचर राशि' : 'Transit Sign'}</th>
                <th className="py-3 px-3">{isHi ? 'अंश' : 'Degree'}</th>
                <th className="py-3 px-3">{isHi ? 'वर्तमान अवधि (From - To)' : 'Current Period (From - To)'}</th>
                <th className="py-3 px-3">{isHi ? 'अगली राशि (From - To)' : 'Next Period (From - To)'}</th>
                <th className="py-3 px-3">{isHi ? 'चंद्र से' : 'From Moon'}</th>
                <th className="py-3 px-3">{isHi ? 'लग्न से' : 'From Lagna'}</th>
                <th className="py-3 px-3">{isHi ? 'सूर्य से' : 'From Sun'}</th>
                <th className="py-3 px-3">{isHi ? 'वेध (अवरोध)' : 'Vedha Status'}</th>
                <th className="py-3 px-3">{isHi ? 'SAV' : 'SAV'}</th>
                <th className="py-3 px-3">{isHi ? 'गति' : 'Motion'}</th>
                <th className="py-3 px-3 text-right">{isHi ? 'प्रमाण' : 'Evidence'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-astro-border text-astro-text font-medium">
              {dynamicEphemeris.map((p) => {
                const houseFromMoon = calculateHouseDistance(moonSignIndex, p.signIndex);
                const houseFromLagna = calculateHouseDistance(lagnaSignIndex, p.signIndex);
                const houseFromSun = calculateHouseDistance(sunSignIndex, p.signIndex);
                const savPoints = kundali.ashtakvarga?.sarvashtakvarga?.[houseFromLagna - 1] ?? (kundali as any).ashtakavarga?.sarvashtakavarga?.[houseFromLagna - 1] ?? 28;
                const period = allTransitPeriods[p.planet];
                const vedha = allVedhaStatus[p.planet];
                const isRowSelected = selectedTransitPlanet === p.planet;

                return (
                  <tr 
                    key={p.planet} 
                    onClick={() => setSelectedTransitPlanet(p.planet)}
                    className={`transition-colors cursor-pointer ${
                      isRowSelected ? 'bg-amber-500/10 dark:bg-amber-500/15' : 'hover:bg-astro-card'
                    }`}
                  >
                    <td className="py-3 px-3 font-bold text-astro-primary flex items-center gap-1.5">
                      {isRowSelected && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                      <span>{p.planet}</span>
                    </td>
                    <td className="py-3 px-3 font-semibold text-astro-text">
                      {isHi ? ZODIAC_HI[p.sign] || p.sign : p.sign}
                    </td>
                    <td className="py-3 px-3 font-mono text-astro-secondary-text">{p.formattedDegree}</td>
                    <td className="py-3 px-3 min-w-[170px]">
                      {period ? (
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1.5 font-mono text-[11px]">
                            <span className="text-astro-text font-semibold">{period.startDateFormatted}</span>
                            <span className="text-astro-secondary-text">→</span>
                            <span className="text-astro-primary font-bold">{period.endDateFormatted}</span>
                          </div>
                          <div className="flex items-center justify-between text-[10px] text-astro-secondary-text">
                            <span>{period.durationFormatted}</span>
                            <span className="font-mono text-astro-primary">{period.daysRemaining}d left</span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-astro-secondary-text text-[11px]">-</span>
                      )}
                    </td>
                    <td className="py-3 px-3 min-w-[180px]">
                      {period ? (
                        <div className="space-y-0.5">
                          <div className="flex items-center justify-between gap-1 text-[11px]">
                            <span className="font-bold text-astro-primary">
                              {period.nextSign}
                            </span>
                            <span className="text-[9px] font-mono font-bold text-astro-primary bg-astro-primary/10 px-1 py-0.2 rounded">
                              {period.daysUntilNextIngress}d left
                            </span>
                          </div>
                          <div className="text-[10px] font-mono text-astro-secondary-text">
                            {period.nextPeriodStartDateFormatted} → {period.nextPeriodEndDateFormatted}
                          </div>
                        </div>
                      ) : (
                        <span className="text-astro-secondary-text text-[11px]">-</span>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <span className="px-2 py-0.5 rounded-md bg-astro-card border border-astro-border text-astro-primary font-bold">
                        H{houseFromMoon}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <span className="px-2 py-0.5 rounded-md bg-astro-card border border-astro-border text-astro-secondary-text">
                        H{houseFromLagna}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <span className="px-2 py-0.5 rounded-md bg-astro-card border border-astro-border text-astro-secondary-text">
                        H{houseFromSun}
                      </span>
                    </td>
                    <td className="py-3 px-3 min-w-[140px]">
                      {vedha?.hasVedha ? (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 dark:bg-amber-950/60 text-amber-900 dark:text-amber-200 border border-amber-300 dark:border-amber-800" title={vedha.interpretationEn}>
                          {isHi ? `वेध: ${vedha.obstructingPlanet} (H${vedha.obstructingHouseFromMoon})` : `Vedha by ${vedha.obstructingPlanet} (H${vedha.obstructingHouseFromMoon})`}
                        </span>
                      ) : vedha?.isExempted ? (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 dark:bg-blue-950/60 text-blue-900 dark:text-blue-200 border border-blue-300 dark:border-blue-800" title={vedha.exemptionReason}>
                          {isHi ? 'वेध छूट (Exempt)' : 'Exempted (Filial)'}
                        </span>
                      ) : vedha?.isFavorableFromMoon ? (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-900 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-800">
                          {isHi ? 'शुभ (अवेधित)' : 'Unobstructed'}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-[10px] bg-stone-100 dark:bg-stone-900 text-stone-600 dark:text-stone-400 border border-stone-200 dark:border-stone-800">
                          {isHi ? 'तटस्थ / अनुशासन' : 'Neutral / Disc.'}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <span 
                        className={`font-mono text-xs px-2 py-0.5 rounded border font-bold ${
                          savPoints >= 28
                            ? 'bg-astro-primary/10 text-astro-primary border-astro-primary/30'
                            : 'bg-astro-card text-astro-secondary-text border-astro-border'
                        }`}
                      >
                        {savPoints}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      {p.isRetrograde ? (
                        <span className="text-astro-accent text-[10px] font-bold bg-astro-accent/15 px-2 py-0.5 rounded border border-astro-accent/30">
                          ℞
                        </span>
                      ) : (
                        <span className="text-astro-secondary-text text-[10px]">
                          D
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTransitPlanet(p.planet);
                          setShowEvidenceDrawer(true);
                          setEvidenceFocusPlanet(p.planet);
                        }}
                        className="px-2 py-1 rounded bg-astro-card hover:bg-astro-primary hover:text-white border border-astro-border text-astro-secondary-text text-[10px] font-semibold transition-colors inline-flex items-center gap-1"
                        title={isHi ? 'शास्त्रीय प्रमाण देखें' : 'Inspect 6-part evidence'}
                      >
                        <FileCheck2 className="w-3 h-3" />
                        <span>{isHi ? 'प्रमाण' : 'Evidence'}</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* V6.0 TRANSIT → NATAL CONTACT MATRIX & TIMING PHASES */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border pb-3">
          <div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-astro-accent" />
              <h3 className="font-bold text-astro-primary font-serif text-lg">
                {isHi ? 'गोचर ➔ जन्म संपर्क एवं तात्कालिक चरण' : 'Transit → Natal Contacts & Timing Phases'}
              </h3>
            </div>
            <p className="text-xs text-astro-secondary-text mt-0.5">
              {isHi
                ? 'शास्त्रीय पृथक्करण: खगोलीय स्थिति बनाम जन्म प्रभाव एवं पराशरी दृष्टि संबंध'
                : 'Classical Jyotish Separation: Ephemeric Position vs Natal Impact & Parashari Drishti'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
          {dynamicEphemeris.map((tp) => {
            const hFromLagna = calculateHouseDistance(lagnaSignIndex, tp.signIndex);
            const hFromMoon = calculateHouseDistance(moonSignIndex, tp.signIndex);

            // Compute authentic Parashari natal contacts
            const contacts: { natalPlanet: string; nature: string; orb: number; house: number }[] = [];
            (kundali.planets || []).forEach(np => {
              if (np.name !== 'Ascendant') {
                const dist = calculateHouseDistance(np.signIndex, tp.signIndex);
                const proximity = Math.abs(tp.degreeInSign - (np.degreeInSign || 0));

                if (dist === 1) {
                  contacts.push({ 
                    natalPlanet: np.name, 
                    nature: isHi ? 'युति (Conjunction)' : 'Conjunction', 
                    orb: Number(proximity.toFixed(1)), 
                    house: np.house 
                  });
                } else if (dist === 7) {
                  contacts.push({ 
                    natalPlanet: np.name, 
                    nature: isHi ? 'सप्तम पूर्ण दृष्टि (7th Drishti)' : '7th Full Aspect', 
                    orb: Number(proximity.toFixed(1)), 
                    house: np.house 
                  });
                } else if (tp.planet === 'Mars' && (dist === 4 || dist === 8)) {
                  contacts.push({ 
                    natalPlanet: np.name, 
                    nature: isHi ? `मंगल ${dist}म विशेष दृष्टि` : `Mars ${dist}th Special Aspect`, 
                    orb: Number(proximity.toFixed(1)), 
                    house: np.house 
                  });
                } else if ((tp.planet === 'Jupiter' || tp.planet === 'Rahu' || tp.planet === 'Ketu') && (dist === 5 || dist === 9)) {
                  contacts.push({ 
                    natalPlanet: np.name, 
                    nature: isHi ? `गुरु/छाया ${dist}म विशेष दृष्टि` : `${tp.planet} ${dist}th Special Aspect`, 
                    orb: Number(proximity.toFixed(1)), 
                    house: np.house 
                  });
                } else if (tp.planet === 'Saturn' && (dist === 3 || dist === 10)) {
                  contacts.push({ 
                    natalPlanet: np.name, 
                    nature: isHi ? `शनि ${dist}म विशेष दृष्टि` : `Saturn ${dist}th Special Aspect`, 
                    orb: Number(proximity.toFixed(1)), 
                    house: np.house 
                  });
                }
              }
            });

            // Transit phase
            let phaseLabel = isHi ? 'अग्रसर काल' : 'Approaching';
            let phaseColor = 'bg-blue-100 dark:bg-blue-950/60 text-blue-900 dark:text-blue-200 border-blue-300 dark:border-blue-800';
            if (tp.isRetrograde) {
              phaseLabel = isHi ? 'वक्री गति' : 'Retrograde / Station';
              phaseColor = 'bg-amber-100 dark:bg-amber-950/60 text-amber-900 dark:text-amber-200 border-amber-300 dark:border-amber-800';
            } else if (tp.degreeInSign < 3.0) {
              phaseLabel = isHi ? 'राशि प्रवेश (Ingress)' : 'Sign Ingress';
              phaseColor = 'bg-purple-100 dark:bg-purple-950/60 text-purple-900 dark:text-purple-200 border-purple-300 dark:border-purple-800';
            } else if (tp.degreeInSign > 27.0) {
              phaseLabel = isHi ? 'राशि निकास (Departure)' : 'Departure Phase';
              phaseColor = 'bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-200 border-slate-300 dark:border-slate-700';
            } else if (contacts.some(c => c.orb <= 1.2)) {
              phaseLabel = isHi ? 'सटीक संपर्क (Exact Peak)' : 'Exact Peak Trigger';
              phaseColor = 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-900 dark:text-emerald-200 border-emerald-300 dark:border-emerald-800';
            }

            return (
              <div key={tp.planet} className="p-3.5 bg-astro-card rounded-xl border border-astro-border space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-astro-primary text-sm">{tp.planet}</span>
                    <span className="text-xs text-astro-secondary-text font-mono font-semibold">
                      {tp.sign.slice(0, 3)} {tp.formattedDegree}
                    </span>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${phaseColor}`}>
                    {phaseLabel}
                  </span>
                </div>

                <div className="text-[11px] text-astro-secondary-text grid grid-cols-2 gap-1 pt-1 border-t border-astro-border">
                  <div>From Lagna: <strong className="text-astro-text">House {hFromLagna}</strong></div>
                  <div>From Moon: <strong className="text-astro-text">House {hFromMoon}</strong></div>
                </div>

                {contacts.length > 0 ? (
                  <div className="space-y-1 pt-1 border-t border-astro-border text-xs">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text block">
                      {isHi ? 'जन्म ग्रह संपर्क:' : 'Natal Planetary Contact:'}
                    </span>
                    {contacts.map((c, idx) => (
                      <div key={idx} className="flex items-center justify-between text-[11px]">
                        <span className="text-purple-700 dark:text-purple-300 font-semibold">{c.nature} with {c.natalPlanet}</span>
                        <span className="font-mono text-astro-secondary-text">orb {c.orb}° (H{c.house})</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-astro-secondary-text italic pt-1 border-t border-astro-border">
                    {isHi ? 'कोई निकट जन्म संपर्क नहीं' : 'Free motion without direct natal aspect.'}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
